import {
  ICustomisationCategory,
  IValidCombination,
  SelectedCategoriesAndOptions,
} from '@models/IProductCategoriesAndOptions';

const mapOptionIdToCategoryId = (
  customisationCategories: ICustomisationCategory[]
): Record<string, string> =>
  customisationCategories.reduce(
    (acc, category) =>
      category.options.reduce(
        (innerAcc, option) => ({
          ...innerAcc,
          [option.optionId]: category.categoryId,
        }),
        acc
      ),
    {}
  );

// When we load the customisation categories from the backend side, we load all the existing categories,
// but we only want to display those combinations that are valid (the ones that are present in the validCombinations array).
// So that if the user selects an option from category 1, we update the available options in category 2 so that
// they can be matched with the selected option from category 1 and so on...
const getValidProductCustomisationCategories = (
  customisationCategories: ICustomisationCategory[],
  validCombinations: IValidCombination[],
  currentCategoriesAndOptionsSelection: SelectedCategoriesAndOptions
) => {
  const optionIdToCategoryIdMap = mapOptionIdToCategoryId(
    customisationCategories
  );

  // Mapper function to get the corresponding categoryId -> optionId direct relation starting from the valid optionIds array,
  // so that we can easily check the selected options for each category
  const getCombinationMap = (optionIds: string[]): Record<string, string> =>
    optionIds.reduce((acc, optionId) => {
      const categoryId = optionIdToCategoryIdMap[optionId] || '';

      return { ...acc, [categoryId]: optionId };
    }, {});

  return customisationCategories
    .map((category, index) => {
      if (index === 0) {
        // For the first category, we only display those options that are present in the valid combinations array
        const validOptions = category.options.filter((option) =>
          validCombinations.some((combination) =>
            combination.optionIds.includes(option.optionId)
          )
        );

        return {
          ...category,
          options: validOptions,
        };
      }

      // Compute the previous selections related to the current category index
      const previousCategories = customisationCategories.slice(0, index);
      const previousSelections = previousCategories.map((category) => {
        const selection =
          currentCategoriesAndOptionsSelection[category.categoryId];

        return selection ? selection.optionId : null;
      });

      // If any of the previous categories is not checked, then we have no options for the current category for now
      if (previousSelections.includes(null)) {
        return {
          ...category,
          options: [],
        };
      }

      // We now filter the valid combinations that match the previous selections
      // E.g. we filter the valid combinations array so that the previously selected options
      // are all present in the optionIds array
      const validCombinationsForCurrentCategory = validCombinations.filter(
        (combination) => {
          const combinationMap = getCombinationMap(combination.optionIds);

          return previousCategories.every((category) => {
            const selectedOptionId =
              currentCategoriesAndOptionsSelection[category.categoryId]
                ?.optionId;

            return combinationMap[category.categoryId] === selectedOptionId;
          });
        }
      );

      // Now we get the unique available options ids for the current category
      const validOptionIdsForCurrentCategory = new Set(
        validCombinationsForCurrentCategory.map((combination) => {
          const combinationMap = getCombinationMap(combination.optionIds);

          return combinationMap[category.categoryId];
        })
      );

      // Get the options using the valid options ids
      const currentCategoryOptions = category.options.filter((option) =>
        validOptionIdsForCurrentCategory.has(option.optionId)
      );

      return {
        ...category,
        options: currentCategoryOptions,
      };
    })
    .filter((category) => category.options.length > 0);
};

export default getValidProductCustomisationCategories;
